import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { createClient } from "@west-tennessee-consulting/ui";
import { useUniversalAuth } from "@west-tennessee-consulting/ui";
import { logger } from "@/lib/logger";
import {
  aggregateSalesPerformance,
  dealCompanyIds as collectDealCompanyIds,
} from "@/lib/dashboard/metrics/sales-performance";
import { SalesPerformanceMetrics, Deal } from "@/lib/types/database/deals";
import { fetchAllSupabaseRows } from "@/lib/hooks/api/dashboard/paginate";

// ============================================================================
// CEO SALES PERFORMANCE METRICS HOOK
// ============================================================================
//
// PURPOSE: Calculate sales performance metrics for CEO dashboard
//
// METRICS:
// - Win Rate: (Count of deals with stage='won') / (Count of all deals) × 100
// - Average Contract Value (ACV): Sum of revenue for won deals / Count of won deals
// - Conversion Ratio: (Count of won deals) / (Count of communications tied to the
//   period's deal companies — i.e. deal-related outreach) × 100
//
// The ARITHMETIC no longer lives here. It moved to
// `lib/dashboard/metrics/sales-performance.ts` so the Bearer route behind the
// iOS owner glance (`/api/workspace-addon/owner-glance`) computes the same three
// numbers from the same function rather than from a second copy of them — the
// same "one module, every surface" rule `forecast.ts` already follows. What
// stays here is the READ: which rows, over which window, under the browser's
// RLS. See that module's header for the definitions and for the one deliberate
// inconsistency it preserves (rentals are NOT excluded from win rate / ACV).
//
// ============================================================================

interface UseCeoSalesPerformanceMetricsOptions {
  dateFrom?: string; // ISO date
  dateTo?: string; // ISO date
  enabled?: boolean;
}

export function useCeoSalesPerformanceMetrics(
  options: UseCeoSalesPerformanceMetricsOptions = {}
) {
  const { isAuthenticated } = useUniversalAuth();
  const supabase = createClient();
  const { enabled = true, dateFrom, dateTo } = options;

  // Validate date range
  if (dateFrom && dateTo) {
    const from = new Date(dateFrom);
    const to = new Date(dateTo);
    if (from > to) {
      throw new Error("Start date must be before end date");
    }
  }

  // Fetch all deals within the date range
  // Note: We filter by close_date (when deals close) not created_at (when deals are created)
  // This ensures consistent metrics across all dashboard sections
  const { data: deals, isLoading: dealsLoading, error: dealsError } = useQuery({
    queryKey: ["ceo-sales-performance-deals", dateFrom, dateTo],
    queryFn: async (): Promise<Deal[]> => {
      if (!isAuthenticated) {
        throw new Error("Authentication required");
      }

      // Page to completion: PostgREST caps a single response near 1000 rows, which
      // would silently truncate win-rate / ACV math past that. fetchAllSupabaseRows
      // loops .range() until a short page. The builder MUST be constructed fresh per
      // call (the Supabase query builder is single-use).
      try {
        return await fetchAllSupabaseRows<Deal>(({ from, to }) => {
          let query = supabase.from("deals").select("*");

          // Apply date filtering on close_date for consistency with forecast metrics
          if (dateFrom) {
            query = query.gte("close_date", dateFrom);
          }
          if (dateTo) {
            query = query.lte("close_date", dateTo);
          }

          return query.order("created_at", { ascending: false }).range(from, to);
        });
      } catch (error) {
        logger.error("Error fetching deals", error);
        throw error;
      }
    },
    enabled: isAuthenticated && enabled,
    staleTime: 1 * 60 * 1000, // 1 minute for critical sales metrics
  });

  // The set of company IDs that own a deal closing in this period. The
  // conversion-ratio denominator is scoped to outreach (communications) tied to
  // these companies — "deal-related outreach" — not every communication logged.
  const dealCompanyIds = useMemo<string[]>(
    () => (deals ? collectDealCompanyIds(deals) : []),
    [deals]
  );

  // Fetch the count of communications tied to the period's deal companies.
  // We filter by occurred_at (activity during the period) AND company_id IN the
  // period's deal companies, so the denominator is deal-related outreach. Depends
  // on the deals query so the company set is known before counting.
  const {
    data: communicationCount,
    isLoading: communicationsLoading,
    error: communicationsError,
  } = useQuery({
    queryKey: [
      "ceo-sales-performance-communications",
      dateFrom,
      dateTo,
      dealCompanyIds,
    ],
    queryFn: async (): Promise<number> => {
      if (!isAuthenticated) {
        throw new Error("Authentication required");
      }

      // No deal companies in the period → no deal-related outreach to count.
      if (dealCompanyIds.length === 0) {
        return 0;
      }

      let query = supabase
        .from("communications")
        .select("id", { count: "exact", head: true })
        // Scope to outreach tied to the period's deal companies. Communications
        // with a null company_id are naturally excluded by this filter.
        .in("company_id", dealCompanyIds);

      // Apply date filtering on occurred_at for communications
      // This captures communications that happened during the period
      if (dateFrom) {
        query = query.gte("occurred_at", dateFrom);
      }
      if (dateTo) {
        query = query.lte("occurred_at", dateTo);
      }

      const { count, error } = await query;

      if (error) {
        logger.error("Error fetching communications count", error);
        throw error;
      }

      return count || 0;
    },
    // Wait for deals before counting so the company scope is known.
    enabled: isAuthenticated && enabled && deals !== undefined,
    staleTime: 1 * 60 * 1000, // 1 minute for critical sales metrics
  });

  // Calculate metrics using useMemo to prevent unnecessary recalculations.
  // The arithmetic itself is `aggregateSalesPerformance` — shared with the
  // Bearer owner-glance route so the phone and the laptop cannot disagree.
  const metrics = useMemo((): SalesPerformanceMetrics => {
    const aggregate = aggregateSalesPerformance(deals ?? [], communicationCount || 0);
    return {
      winRate: aggregate.winRate,
      averageContractValue: aggregate.averageContractValue,
      conversionRatio: aggregate.conversionRatio,
      totalDeals: aggregate.totalDeals,
      wonDeals: aggregate.wonDeals,
      lostDeals: aggregate.lostDeals,
      totalCommunications: communicationCount || 0,
      periodStart: dateFrom || new Date().toISOString().split("T")[0],
      periodEnd: dateTo || new Date().toISOString().split("T")[0],
    };
  }, [deals, communicationCount, dateFrom, dateTo]);

  return {
    metrics,
    isLoading: dealsLoading || communicationsLoading,
    error: dealsError || communicationsError,
    deals,
    communicationCount,
  };
}
